import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Globe, MapPin, Activity, Eye, Search, ChevronLeft, ChevronRight, CalendarDays, Pencil } from "lucide-react";
import { ccInput, ccCard } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

// Site analytics: visitor geolocation, activity monitoring, infinite calendar, global search
export default function MasterAnalytics({ onReviewAccount }) {
  const [data, setData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState("");
  const [editingActivity, setEditingActivity] = useState(null);
  const [actForm, setActForm] = useState({ action: "", details: "", user_email: "" });

  const saveActivity = async (e, activity) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/master/activity/${activity.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(actForm),
      });
      if (!res.ok) throw new Error();
      setEditingActivity(null);
      load();
    } catch (err) {
      console.error("Activity save failed", err);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/analytics`, { credentials: "include" });
      if (!res.ok) return;
      setData(await res.json());
      setLastUpdated(new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" }));
    } catch (err) {
      console.error("Analytics load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const runSearch = async (q, date) => {
    if (!q.trim() && !date) {
      setResults(null);
      return;
    }
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (date) params.set("date", date);
      const res = await fetch(`${API}/api/master/search?${params}`, { credentials: "include" });
      if (res.ok) setResults(await res.json());
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => runSearch(query, selectedDate), 350);
    return () => clearTimeout(t);
  }, [query, selectedDate]);

  // Infinite calendar math
  const { year, month } = calMonth;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = new Date(year, month, 1).getDay();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthName = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const shiftMonth = (delta) =>
    setCalMonth((p) => {
      const d = new Date(p.year, p.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const statCards = data
    ? [
        { label: "Total Visits", value: data.totalVisits, icon: Eye, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", testId: "an-visits", scrollTo: "recent-visits" },
        { label: "Unique Visitors", value: data.uniqueVisitors, icon: Globe, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]", testId: "an-unique", scrollTo: "recent-visits" },
        { label: "Countries", value: data.countriesTracked, icon: MapPin, wrap: "bg-[#E2F4E1]", color: "text-[#7CC98F]", testId: "an-countries", scrollTo: "activity-calendar" },
        { label: "Activities Logged", value: data.totalActivities, icon: Activity, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", testId: "an-activities", scrollTo: "activity-feed" },
      ]
    : [];

  const scrollToSection = (testId) => {
    document.querySelector(`[data-testid="${testId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-6" data-testid="master-analytics">
      {/* Header with refresh controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-extrabold font-heading text-[#374151]">Site Analytics & Activity Monitor</h3>
          <p className="text-[11px] text-[#9CA3AF] font-medium">
            {lastUpdated ? `Last updated ${lastUpdated}` : "Loading…"} {autoRefresh && "· auto-refreshing every 15s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((a) => !a)}
            data-testid="auto-refresh-toggle"
            className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${
              autoRefresh ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-white border-[#FCE6E9] text-[#9CA3AF]"
            }`}
          >
            Auto 15s {autoRefresh ? "On" : "Off"}
          </button>
          <button
            onClick={load}
            data-testid="analytics-refresh-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-xs font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-stats">
        {statCards.map((s) => (
          <button
            key={s.testId}
            onClick={() => scrollToSection(s.scrollTo)}
            className={`${ccCard} p-4 text-left cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
            data-testid={s.testId}
          >
            <span className={`w-9 h-9 rounded-full ${s.wrap} flex items-center justify-center mb-3`}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </span>
            <p className="text-2xl font-black font-heading">{s.value}</p>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Global search + infinite calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${ccCard} p-6`} data-testid="global-search-panel">
          <h4 className="text-sm font-extrabold font-heading mb-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#5CB3DC]" /> Global Search
          </h4>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search staff, visitors, activity — name, email, country, facility…"
            className={ccInput}
            data-testid="global-search-input"
          />
          {selectedDate && (
            <p className="text-[11px] font-bold text-[#3D7E9E] mt-2" data-testid="search-date-filter">
              Filtered to {selectedDate}{" "}
              <button onClick={() => setSelectedDate("")} className="text-rose-400 hover:underline ml-1" data-testid="search-date-clear">
                clear
              </button>
            </p>
          )}
          <div className="mt-4 space-y-3 max-h-72 overflow-y-auto" data-testid="global-search-results">
            {searching && <p className="text-xs text-[#9CA3AF]">Searching…</p>}
            {!searching && results && (
              <>
                {results.staff.length + results.visits.length + results.activities.length === 0 && (
                  <p className="text-xs text-[#9CA3AF]" data-testid="search-empty">
                    No matches found.
                  </p>
                )}
                {results.staff.map((s) => (
                  <div key={s.id} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl" data-testid={`search-staff-${s.id}`}>
                    <p className="text-xs font-extrabold">{s.name} <span className="text-[#9CA3AF] font-semibold">· {s.position}</span></p>
                    <p className="text-[11px] text-[#9CA3AF]">{s.email}</p>
                  </div>
                ))}
                {results.visits.map((v, i) => (
                  <div key={`v${i}`} className="p-3 bg-[#FFF9F2] border border-[#E6F2FA] rounded-xl" data-testid={`search-visit-${i}`}>
                    <p className="text-xs font-extrabold">
                      {v.path} <span className="text-[#9CA3AF] font-semibold">· {v.ip}</span>
                    </p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      {[v.city, v.region, v.country].filter(Boolean).join(", ") || "Location unknown"} · {fmtTs(v.ts)}
                    </p>
                  </div>
                ))}
                {results.activities.map((a, i) => (
                  <div key={`a${i}`} className="p-3 bg-[#FFF9F2] border border-[#E2F4E1] rounded-xl" data-testid={`search-activity-${i}`}>
                    <p className="text-xs font-extrabold">{a.action}</p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      {a.details} {a.user_email && `· ${a.user_email}`} · {fmtTs(a.ts)}
                    </p>
                  </div>
                ))}
              </>
            )}
            {!results && !searching && <p className="text-xs text-[#9CA3AF]">Type to search, or pick a date on the calendar.</p>}
          </div>
        </div>

        {/* Infinite calendar */}
        <div className={`${ccCard} p-6`} data-testid="activity-calendar">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-extrabold font-heading flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[#E5A0BC]" /> Activity Calendar
            </h4>
            <div className="flex items-center gap-2">
              <button onClick={() => shiftMonth(-1)} data-testid="cal-prev" className="w-7 h-7 rounded-full bg-[#FCE6E9] text-[#B2607F] flex items-center justify-center hover:bg-[#F8C8DC] transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-extrabold font-heading w-32 text-center" data-testid="cal-month-label">{monthName}</span>
              <button onClick={() => shiftMonth(1)} data-testid="cal-next" className="w-7 h-7 rounded-full bg-[#FCE6E9] text-[#B2607F] flex items-center justify-center hover:bg-[#F8C8DC] transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-extrabold uppercase text-[#9CA3AF] mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
            {cells.map((day, i) => {
              if (!day) return <span key={`empty-${i}`} />;
              const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const count = data?.dayCounts?.[key] || 0;
              const selected = selectedDate === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDate(selected ? "" : key)}
                  data-testid={`cal-day-${key}`}
                  className={`aspect-square rounded-xl border text-xs font-bold flex flex-col items-center justify-center transition-all duration-150 ${
                    selected
                      ? "bg-[#374151] text-white border-[#374151]"
                      : count > 0
                      ? "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]"
                      : "bg-[#FFF9F2] border-[#FCE6E9] text-[#9CA3AF] hover:border-[#B3E0F2]"
                  }`}
                >
                  {day}
                  {count > 0 && (
                    <span className={`text-[8px] font-extrabold px-1 rounded-full ${selected ? "bg-[#B3E0F2] text-[#374151]" : "bg-[#B3E0F2]/60 text-[#3D7E9E]"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent visits + activity feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${ccCard} p-6`} data-testid="recent-visits">
          <h4 className="text-sm font-extrabold font-heading mb-3">Recent Visits (who, from where)</h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(data?.recentVisits || []).length === 0 && <p className="text-xs text-[#9CA3AF]">No visits recorded yet.</p>}
            {(data?.recentVisits || []).map((v, i) => (
              <div key={`${v.ts}-${v.ip}`} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl flex items-center justify-between gap-2" data-testid={`visit-row-${i}`}>
                <div className="min-w-0">
                  <p className="text-xs font-extrabold truncate">
                    {v.path} <span className="text-[#9CA3AF] font-semibold">· {v.ip}</span>
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[#E5A0BC] shrink-0" />
                    {[v.city, v.region, v.country].filter(Boolean).join(", ") || "Resolving location…"}
                  </p>
                </div>
                <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{fmtTs(v.ts)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`${ccCard} p-6`} data-testid="activity-feed">
          <h4 className="text-sm font-extrabold font-heading mb-3">Live Activity Feed</h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(data?.recentActivities || []).length === 0 && <p className="text-xs text-[#9CA3AF]">No activity recorded yet.</p>}
            {(data?.recentActivities || []).map((a, i) => (
              <div key={a.id || i} className="p-3 bg-[#FFF9F2] border border-[#E6F2FA] rounded-xl" data-testid={`activity-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full">{a.action}</span>
                  <div className="flex items-center gap-2">
                    {a.edited && <span className="text-[9px] font-bold text-[#D9A93E]">edited</span>}
                    <span className="text-[10px] text-[#9CA3AF]">{fmtTs(a.ts)}</span>
                    {a.id && (
                      <button
                        onClick={() => {
                          setActForm({ action: a.action, details: a.details, user_email: a.user_email || "" });
                          setEditingActivity(editingActivity === a.id ? null : a.id);
                        }}
                        data-testid={`activity-edit-${i}`}
                        title="Edit log entry"
                        className="text-[#9CA3AF] hover:text-[#5CB3DC] transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#6B7280] mt-1.5">{a.details}</p>
                {a.user_email && (
                  <button onClick={() => onReviewAccount?.(a.user_email)} className="text-[10px] text-[#5CB3DC] mt-0.5 hover:underline font-semibold" data-testid={`activity-account-${i}`}>
                    {a.user_email}
                  </button>
                )}
                {editingActivity === a.id && (
                  <form onSubmit={(e) => saveActivity(e, a)} className="mt-2 space-y-2" data-testid={`activity-edit-form-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={actForm.action} onChange={(e) => setActForm({ ...actForm, action: e.target.value })} className={ccInput} placeholder="ACTION" data-testid={`activity-edit-action-${i}`} />
                      <input value={actForm.user_email} onChange={(e) => setActForm({ ...actForm, user_email: e.target.value })} className={ccInput} placeholder="user email" data-testid={`activity-edit-email-${i}`} />
                    </div>
                    <textarea value={actForm.details} onChange={(e) => setActForm({ ...actForm, details: e.target.value })} className={`${ccInput} min-h-[50px]`} placeholder="Details" data-testid={`activity-edit-details-${i}`} />
                    <button type="submit" className="px-3.5 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold" data-testid={`activity-edit-save-${i}`}>
                      Save Log
                    </button>
                  </form>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
